import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Breadcrumbs,
  Link as MuiLink,
  Fade,
  Slide,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Shield as ShieldIcon,
  AccountCircle as AccountCircleIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  ContactSupport as ContactSupportIcon,
  VerifiedUser as VerifiedUserIcon,
  Cookie as CookieIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import privacyPolicyBn from '../i18n/bn/privacyPolicy';
import privacyPolicyEn from '../i18n/en/privacyPolicy';
import privacyPolicyNp from '../i18n/np/privacyPolicy';
import privacyPolicyPk from '../i18n/pk/privacyPolicy';
import privacyPolicyBt from '../i18n/bt/privacyPolicy';
import privacyPolicyDe from '../i18n/de/privacyPolicy';
import privacyPolicySl from '../i18n/sl/privacyPolicy';
import privacyPolicyAf from '../i18n/af/privacyPolicy';
import privacyPolicyCn from '../i18n/cn/privacyPolicy';

const PrivacyPolicy = () => {
  const currentLanguage = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = currentLanguage === 'en' ? privacyPolicyEn : currentLanguage === 'np' ? privacyPolicyNp : currentLanguage === 'pk' ? privacyPolicyPk : currentLanguage === 'bt' ? privacyPolicyBt : currentLanguage === 'de' ? privacyPolicyDe : currentLanguage === 'sl' ? privacyPolicySl : currentLanguage === 'af' ? privacyPolicyAf : currentLanguage === 'cn' ? privacyPolicyCn : privacyPolicyBn;

  const privacyPrinciples = [
    {
      icon: <SecurityIcon />,
      title: t.principles[0].title,
      description: t.principles[0].description,
      color: '#4CAF50'
    },
    {
      icon: <VisibilityIcon />,
      title: t.principles[1].title,
      description: t.principles[1].description,
      color: '#2196F3'
    },
    {
      icon: <AccountCircleIcon />,
      title: t.principles[2].title,
      description: t.principles[2].description,
      color: '#FF9800'
    },
    {
      icon: <ShieldIcon />,
      title: t.principles[3].title,
      description: t.principles[3].description,
      color: '#9C27B0'
    }
  ];

  const dataTypes = [
    {
      category: t.dataTypes[0].category,
      items: t.dataTypes[0].items,
      icon: <AccountCircleIcon />,
      purpose: t.dataTypes[0].purpose
    },
    {
      category: t.dataTypes[1].category,
      items: t.dataTypes[1].items,
      icon: <LocationOnIcon />,
      purpose: t.dataTypes[1].purpose
    },
    {
      category: t.dataTypes[2].category,
      items: t.dataTypes[2].items,
      icon: <CreditCardIcon />,
      purpose: t.dataTypes[2].purpose
    },
    {
      category: t.dataTypes[3].category,
      items: t.dataTypes[3].items,
      icon: <PhoneAndroidIcon />,
      purpose: t.dataTypes[3].purpose
    }
  ];

  const userRights = [
    {
      right: t.rights.items[0].right,
      description: t.rights.items[0].description,
      icon: <VisibilityIcon />
    },
    {
      right: t.rights.items[1].right,
      description: t.rights.items[1].description,
      icon: <EditIcon />
    },
    {
      right: t.rights.items[2].right,
      description: t.rights.items[2].description,
      icon: <DeleteIcon />
    },
    {
      right: t.rights.items[3].right,
      description: t.rights.items[3].description,
      icon: <DownloadIcon />
    }
  ];

  const securityMeasures = t.security.measures;

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 8,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container>
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />} 
              aria-label="breadcrumb"
              sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.7)' } }}
            >
              <MuiLink component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {t.breadcrumbs.home}
              </MuiLink>
              <Typography sx={{ color: 'white' }}>{t.breadcrumbs.title}</Typography>
            </Breadcrumbs>

            <Fade in timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  {t.hero.title}
                </Typography>
                <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
                  {t.hero.subtitle}
                </Typography>
              </Box>
            </Fade>
          </Container>
        </Box>

        <Container sx={{ py: 6 }}>
          {/* Privacy Principles */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.hero.title}
            </Typography>
            <Grid container spacing={2.5} sx={{ px: 2 }}>
              {privacyPrinciples.map((principle, index) => (
                <Grid item xs={12} sm={6} md={3} key={index} sx={{ mb: 2 }}>
                  <Slide direction="up" in timeout={800 + index * 200}>
                    <Card
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        },
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        mx: 1,
                        mt: 1
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: principle.color,
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {principle.icon}
                      </Avatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                        {principle.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                        {principle.description}
                      </Typography>
                    </Card>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Data Collection */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.dataTitle || 'हामीले संकलन गर्ने जानकारी'}
            </Typography>
            <Grid container spacing={4}>
              {dataTypes.map((dataType, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Fade in timeout={1200 + index * 200}>
                    <Card
                      sx={{
                        p: 3,
                        height: '100%',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#667eea', mr: 2 }}>
                          {dataType.icon}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {dataType.category}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                         {currentLanguage === 'en' ? 'Purpose' : 'উদ্দেশ্য'}: {dataType.purpose}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {dataType.items.map((item, itemIndex) => (
                          <Chip
                            key={itemIndex}
                            label={item}
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Data Usage */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.usage.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
                    {t.usage.primaryTitle}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.primary[0]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.primary[1]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.primary[2]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.primary[3]} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
                    {t.usage.improveTitle}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="secondary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.improve[0]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="secondary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.improve[1]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="secondary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.improve[2]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DataUsageIcon color="secondary" />
                      </ListItemIcon>
                       <ListItemText primary={t.usage.improve[3]} />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* User Rights */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.rights.title}
            </Typography>
            <Grid container spacing={3}>
              {userRights.map((right, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      height: '100%',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#667eea', mx: 'auto', mb: 2 }}>
                      {right.icon}
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {right.right}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {right.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Security Measures */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.security.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t.security.intro}
                </Typography>
              </Alert>
              <Grid container spacing={2}>
                {securityMeasures.map((measure, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LockIcon sx={{ color: '#4CAF50', mr: 1 }} />
                      <Typography variant="body1">{measure}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>

          {/* Cookies Policy */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.cookies.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CookieIcon sx={{ fontSize: 40, color: '#FF9800', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t.cookies.usageTitle}
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                {t.cookies.p1}
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                {t.cookies.p2}
              </Typography>
            </Paper>
          </Box>

          {/* Third Party Sharing */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.sharing.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t.sharing.intro}
                </Typography>
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
                    {t.sharing.authorizedTitle}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ShareIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.authorized[0]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ShareIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.authorized[1]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ShareIcon color="primary" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.authorized[2]} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#F44336' }}>
                    {t.sharing.notDoTitle}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CancelIcon color="error" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.notDo[0]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CancelIcon color="error" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.notDo[1]} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CancelIcon color="error" />
                      </ListItemIcon>
                       <ListItemText primary={t.sharing.notDo[2]} />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Data Retention */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.retention.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StorageIcon sx={{ color: '#667eea', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                       {t.retention.retentionTitle}
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                    {t.retention.retentionText}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DeleteIcon sx={{ color: '#F44336', mr: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                       {t.retention.deleteTitle}
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                    {t.retention.deleteText}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Policy Updates */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.updates.title}
            </Typography>
            <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t.updates.alert}
                </Typography>
              </Alert>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                {t.updates.notify}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>{t.updates.lastUpdatedPrefix}</strong> {t.updates.lastUpdatedDate}
              </Typography>
            </Paper>
          </Box>

          {/* Contact for Privacy */}
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              mt: '80px'
            }}
          >
            <ContactSupportIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {t.contact.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              {t.contact.subtitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <MuiLink
                component={Link}
                to="/contact"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                {t.contact.button}
              </MuiLink>
              <MuiLink
                href="mailto:privacy@grameenbazar.com"
                sx={{
                  border: '2px solid white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {t.contact.email}
              </MuiLink>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default PrivacyPolicy; 